import { AchievementName } from '@helios/achievement';
import { GetAchievementResponseDto } from '@helios/frontend-api-v2-client';
import { StepComponent, Takeover, trackEvent, useFlowStep } from '@helios/frontend-core';
import { Themes } from '@palmetto/dsm-tokens';
import { kebabCase } from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import { useViewAchievement } from '../../../api/achievements/useViewAchievement';
import { VerticalSlideInOut } from '../../../components/Animations/VerticalSlideInOut/VerticalSlideInOut';
import BadgeEarnedView from './components/BadgeEarnedView';
import { StepperButtons } from './components/StepperButtons';

const BadgeEarnedTakeover = (props: {
  isOpen: boolean;
  onDismiss: () => void;
  achievements: GetAchievementResponseDto[];
}) => {
  const { isOpen, onDismiss, achievements } = props;
  const TRACKING_ID = `badge-earned-takeover`;
  const { mutate: viewAchievement } = useViewAchievement();
  const viewedAchievementIds = useRef<Set<string>>(new Set());

  const stepsMap: StepComponent<AchievementName>[] = achievements.map((achievement) => ({
    stepName: achievement.name,
    component: BadgeEarnedView,
    props: {
      achievementName: achievement.name,
    },
  }));

  const { activeStepContext, nextStep, previousStep } = useFlowStep(stepsMap, achievements[0]?.name, {
    onComplete: onDismiss,
  });

  const badgeEarnedTakeoverDismiss = () => {
    viewedAchievementIds.current.clear();
    onDismiss();
  };

  useEffect(() => {
    if (isOpen && achievements.length > 0) {
      achievements.forEach((achievement) => {
        if (achievement._id && !viewedAchievementIds.current.has(achievement._id)) {
          viewedAchievementIds.current.add(achievement._id);
          viewAchievement({ id: achievement._id });
        }
      });
    }
  }, [isOpen, achievements, viewAchievement]);

  const memoizedStep = useMemo(() => {
    trackEvent('component loaded', {
      trackingId: `${TRACKING_ID}-${kebabCase(activeStepContext.stepName)}-step`,
    });
    return <activeStepContext.component />;
  }, [activeStepContext.stepName, TRACKING_ID]);

  return (
    <Takeover isOpen={isOpen} onClose={badgeEarnedTakeoverDismiss} trackingId={TRACKING_ID} theme={Themes.volt}>
      <div className="flex flex-col h-full">
        <div className="flex h-full w-full px-32">
          <VerticalSlideInOut direction={activeStepContext.direction || 'forward'}>
            {memoizedStep}
          </VerticalSlideInOut>
        </div>
        <div className="flex flex-col gap-8 px-24">
          <StepperButtons
            previousStep={previousStep}
            nextStep={nextStep}
            onClose={badgeEarnedTakeoverDismiss}
            activeStepName={activeStepContext.stepName}
            steps={stepsMap}
            parentTrackingId={TRACKING_ID}
          />
        </div>
      </div>
    </Takeover>
  );
};

export default BadgeEarnedTakeover;
